import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initialNodes } from '../data/loanFiles';
import {
  childrenOf,
  descendantIds,
  expandToFiles,
  findNode,
  isFile,
  isFolder,
  isWithin,
  pathTo,
  totalUsedKb,
} from '../utils/fileTree';
import type {
  DownloadStatus,
  FileType,
  FsFile,
  FsNode,
  Layout,
  PanelStatus,
  SortKey,
  SortState,
  ViewId,
} from '../types/files';

interface UseFileManagerOptions {
  initialStatus: PanelStatus;
  emptyFiles: boolean;
  defaultLayout: Layout;
}

interface DownloadState {
  status: DownloadStatus;
  progress: number;
  name: string | null;
  fileCount: number;
  sizeKb: number;
}

const IDLE_DOWNLOAD: DownloadState = {
  status: 'idle',
  progress: 0,
  name: null,
  fileCount: 0,
  sizeKb: 0,
};

export function useFileManager({
  initialStatus,
  emptyFiles,
  defaultLayout,
}: UseFileManagerOptions) {
  const seed = useMemo(() => (emptyFiles ? [] : initialNodes), [emptyFiles]);
  const [status, setStatus] = useState<PanelStatus>(initialStatus);
  const [nodes, setNodes] = useState<FsNode[]>(
    initialStatus === 'ready' ? seed : [],
  );
  const [view, setView] = useState<ViewId>('files');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeTypes, setActiveTypes] = useState<FileType[]>([]);
  const [sort, setSort] = useState<SortState>({
    key: 'modifiedAt',
    direction: 'desc',
  });
  const [layout, setLayout] = useState<Layout>(defaultLayout);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [download, setDownload] = useState<DownloadState>(IDLE_DOWNLOAD);
  const timers = useRef<number[]>([]);

  const track = useCallback((id: number) => {
    timers.current.push(id);
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );

  useEffect(() => {
    if (initialStatus !== 'loading') return;
    const timer = window.setTimeout(() => {
      setNodes(seed);
      setStatus('ready');
    }, 1400);
    return () => window.clearTimeout(timer);
  }, [initialStatus, seed]);

  const searching = query.trim().length > 0;

  const scopedNodes = useMemo(() => {
    if (view === 'trash') return nodes.filter((node) => node.trashed);
    const live = nodes.filter((node) => !node.trashed);
    if (view === 'starred') return live.filter((node) => node.starred);
    if (view === 'recent') {
      return [...live.filter(isFile)]
        .sort(
          (a, b) =>
            new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime(),
        )
        .slice(0, 12);
    }
    if (searching) return live;
    return childrenOf(nodes, folderId);
  }, [nodes, view, folderId, searching]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = scopedNodes.filter((node) => {
      const matchesQuery =
        needle.length === 0 ||
        node.name.toLowerCase().includes(needle) ||
        node.owner.toLowerCase().includes(needle) ||
        (isFile(node) && node.extension.toLowerCase().includes(needle));
      const matchesType =
        activeTypes.length === 0 ||
        (isFile(node) && activeTypes.includes(node.type));
      return matchesQuery && matchesType;
    });

    const factor = sort.direction === 'asc' ? 1 : -1;
    const compare = (a: FsNode, b: FsNode) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      const key: SortKey = sort.key;
      if (key === 'sizeKb') {
        const aSize = isFile(a) ? a.sizeKb : 0;
        const bSize = isFile(b) ? b.sizeKb : 0;
        return (aSize - bSize) * factor;
      }
      if (key === 'modifiedAt') {
        return (
          (new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime()) *
          factor
        );
      }
      return String(a[key]).localeCompare(String(b[key])) * factor;
    };

    return [...filtered].sort(compare);
  }, [scopedNodes, query, activeTypes, sort]);

  const typeCounts = useMemo(() => {
    const counts = new Map<FileType, number>();
    scopedNodes.forEach((node) => {
      if (!isFile(node)) return;
      counts.set(node.type, (counts.get(node.type) ?? 0) + 1);
    });
    return counts;
  }, [scopedNodes]);

  const breadcrumbs = useMemo(() => {
    if (!folderId) return [];
    const current = findNode(nodes, folderId);
    const chain = pathTo(nodes, folderId);
    return current && isFolder(current) ? [...chain, current] : chain;
  }, [nodes, folderId]);

  const selectedNodes = useMemo(
    () => nodes.filter((node) => selectedIds.includes(node.id)),
    [nodes, selectedIds],
  );

  const selectedFiles = useMemo(
    () => expandToFiles(nodes, selectedIds),
    [nodes, selectedIds],
  );

  const selectedSizeKb = selectedFiles.reduce(
    (total, file) => total + file.sizeKb,
    0,
  );

  const activeNode = findNode(nodes, activeId) ?? null;

  const previewCandidateIds = useMemo(
    () => rows.filter(isFile).map((node) => node.id),
    [rows],
  );

  const previewNodeRaw = findNode(nodes, previewId);
  const previewFile =
    previewNodeRaw && isFile(previewNodeRaw) ? previewNodeRaw : null;
  const previewIndex = previewId ? previewCandidateIds.indexOf(previewId) : -1;
  const hasPrevPreview = previewIndex > 0;
  const hasNextPreview =
    previewIndex >= 0 && previewIndex < previewCandidateIds.length - 1;

  const stepPreview = useCallback(
    (delta: number) => {
      if (previewIndex < 0) return;
      const nextId = previewCandidateIds[previewIndex + delta];
      if (nextId) {
        setPreviewId(nextId);
        setActiveId(nextId);
      }
    },
    [previewCandidateIds, previewIndex],
  );

  const rowIds = rows.map((node) => node.id);
  const allRowsSelected =
    rowIds.length > 0 && rowIds.every((id) => selectedIds.includes(id));
  const someRowsSelected =
    !allRowsSelected && rowIds.some((id) => selectedIds.includes(id));

  const counts = useMemo(() => {
    const live = nodes.filter((node) => !node.trashed);
    return {
      all: live.filter(isFile).length,
      folders: live.filter(isFolder).length,
      starred: live.filter((node) => node.starred).length,
      trash: nodes.filter((node) => node.trashed).length,
      recent: Math.min(12, live.filter(isFile).length),
    };
  }, [nodes]);

  const usedKb = useMemo(() => totalUsedKb(nodes), [nodes]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const changeView = useCallback((next: ViewId) => {
    setView(next);
    setQuery('');
    setActiveTypes([]);
    setSelectedIds([]);
    if (next === 'files') setFolderId(null);
  }, []);

  const goToFolder = useCallback((id: string | null) => {
    setView('files');
    setFolderId(id);
    setQuery('');
    setSelectedIds([]);
  }, []);

  const openNode = useCallback(
    (node: FsNode) => {
      if (isFolder(node)) {
        if (!node.trashed) goToFolder(node.id);
        return;
      }
      setActiveId(node.id);
      setPreviewId(node.id);
    },
    [goToFolder],
  );

  const closePreview = useCallback(() => setPreviewId(null), []);

  const toggleType = useCallback((type: FileType) => {
    setActiveTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setActiveTypes([]);
    setQuery('');
  }, []);

  const toggleRow = useCallback((id: string, additive = true) => {
    setSelectedIds((current) => {
      if (!additive) return [id];
      return current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
    });
  }, []);

  const toggleAllRows = useCallback(() => {
    setSelectedIds((current) => {
      const every =
        rowIds.length > 0 && rowIds.every((id) => current.includes(id));
      if (every) return current.filter((id) => !rowIds.includes(id));
      return Array.from(new Set([...current, ...rowIds]));
    });
  }, [rowIds]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : {
            key,
            direction: key === 'name' || key === 'owner' ? 'asc' : 'desc',
          },
    );
  }, []);

  const toggleStar = useCallback((id: string) => {
    setNodes((current) =>
      current.map((node) =>
        node.id === id ? { ...node, starred: !node.starred } : node,
      ),
    );
  }, []);

  const renameNode = useCallback((id: string, name: string) => {
    setNodes((current) =>
      current.map((node) =>
        node.id === id
          ? {
              ...node,
              name,
              modifiedAt: new Date().toISOString(),
              modifiedBy: 'Priya Raman',
            }
          : node,
      ),
    );
  }, []);

  const createFolder = useCallback(
    (name: string) => {
      const id = `d-${Date.now()}`;
      setNodes((current) => [
        ...current,
        {
          id,
          name,
          parentId: folderId,
          kind: 'folder',
          owner: 'Priya Raman',
          modifiedBy: 'Priya Raman',
          modifiedAt: new Date().toISOString(),
          starred: false,
          trashed: false,
        },
      ]);
      return id;
    },
    [folderId],
  );

  const moveNodes = useCallback(
    (ids: string[], targetId: string | null) => {
      const movable = ids.filter((id) => {
        if (id === targetId) return false;
        if (targetId && isWithin(nodes, targetId, id)) return false;
        return true;
      });
      setNodes((current) =>
        current.map((node) =>
          movable.includes(node.id) ? { ...node, parentId: targetId } : node,
        ),
      );
      setSelectedIds([]);
      return movable.length;
    },
    [nodes],
  );

  const trashNodes = useCallback(
    (ids: string[]) => {
      const withChildren = new Set(ids);
      ids.forEach((id) => {
        const node = findNode(nodes, id);
        if (node && isFolder(node)) {
          descendantIds(nodes, id).forEach((child) => withChildren.add(child));
        }
      });
      setNodes((current) =>
        current.map((node) =>
          withChildren.has(node.id) ? { ...node, trashed: true } : node,
        ),
      );
      setSelectedIds([]);
      setActiveId((current) => (current && withChildren.has(current) ? null : current));
      setPreviewId((current) =>
        current && withChildren.has(current) ? null : current,
      );
    },
    [nodes],
  );

  const restoreNodes = useCallback((ids: string[]) => {
    setNodes((current) =>
      current.map((node) =>
        ids.includes(node.id) ? { ...node, trashed: false } : node,
      ),
    );
    setSelectedIds([]);
  }, []);

  const deleteForever = useCallback((ids: string[]) => {
    setNodes((current) => current.filter((node) => !ids.includes(node.id)));
    setSelectedIds([]);
    setActiveId((current) => (current && ids.includes(current) ? null : current));
    setPreviewId((current) => (current && ids.includes(current) ? null : current));
  }, []);

  const emptyTrash = useCallback(() => {
    setNodes((current) => current.filter((node) => !node.trashed));
    setSelectedIds([]);
  }, []);

  const uploadFiles = useCallback(
    (incoming: { name: string; extension: string; type: FileType; sizeKb: number }[]) => {
      const now = new Date().toISOString();
      const created: FsFile[] = incoming.map((item, index) => ({
        id: `f-${Date.now()}-${index}`,
        name: item.name,
        parentId: folderId,
        kind: 'file',
        type: item.type,
        extension: item.extension,
        sizeKb: item.sizeKb,
        version: 1,
        owner: 'Priya Raman',
        modifiedBy: 'Priya Raman',
        modifiedAt: now,
        starred: false,
        trashed: false,
      }));
      setNodes((current) => [...current, ...created]);
      return created.length;
    },
    [folderId],
  );

  const startZipDownload = useCallback(
    (ids: string[]) => {
      const files = expandToFiles(nodes, ids);
      if (files.length === 0) return 0;
      const sizeKb = files.reduce((total, file) => total + file.sizeKb, 0);
      setDownload({
        status: 'zipping',
        progress: 8,
        name: null,
        fileCount: files.length,
        sizeKb,
      });
      [24, 46, 68, 88, 100].forEach((value, index) => {
        track(
          window.setTimeout(
            () =>
              setDownload((current) =>
                current.status === 'zipping'
                  ? { ...current, progress: value }
                  : current,
              ),
            260 * (index + 1),
          ),
        );
      });
      track(
        window.setTimeout(() => {
          setDownload((current) => ({
            ...current,
            status: 'complete',
            progress: 100,
            name: `${new Date().toISOString().slice(0, 10)}-loan-files-${files.length}.zip`,
          }));
        }, 1540),
      );
      return files.length;
    },
    [nodes, track],
  );

  const dismissDownload = useCallback(() => setDownload(IDLE_DOWNLOAD), []);

  const refresh = useCallback(() => {
    setStatus('loading');
    setSelectedIds([]);
    track(
      window.setTimeout(() => {
        setStatus('ready');
      }, 900),
    );
  }, [track]);

  const retry = useCallback(() => {
    setStatus('loading');
    track(
      window.setTimeout(() => {
        setNodes(seed);
        setStatus('ready');
      }, 900),
    );
  }, [seed, track]);

  return {
    status,
    nodes,
    rows,
    view,
    changeView,
    folderId,
    goToFolder,
    breadcrumbs,
    openNode,
    query,
    setQuery,
    searching,
    activeTypes,
    typeCounts,
    toggleType,
    clearFilters,
    sort,
    setSort,
    toggleSort,
    layout,
    setLayout,
    selectedIds,
    selectedNodes,
    selectedFiles,
    selectedSizeKb,
    allRowsSelected,
    someRowsSelected,
    toggleRow,
    toggleAllRows,
    clearSelection,
    activeNode,
    setActiveId,
    previewFile,
    previewPosition: {
      index: previewIndex + 1,
      total: previewCandidateIds.length,
    },
    hasPrevPreview,
    hasNextPreview,
    stepPreview,
    closePreview,
    counts,
    usedKb,
    toggleStar,
    renameNode,
    createFolder,
    moveNodes,
    trashNodes,
    restoreNodes,
    deleteForever,
    emptyTrash,
    uploadFiles,
    download,
    startZipDownload,
    dismissDownload,
    refresh,
    retry,
  };
}
